/**
 * bleService — wrapper su @capacitor-community/bluetooth-le per il pairing
 * del materasso SmartDream.
 *
 * PROTOCOLLO FIRMWARE SmartDream (confermato dal report TestFlight reale):
 *   Service UUID:        000000FF-0000-1000-8000-00805F9B34FB (0x00FF)
 *   Characteristic UUID: 0000FF01-0000-1000-8000-00805F9B34FB (Read + Write + Notify)
 *
 * I comandi firmware usano apici singoli + CRLF. Le risposte arrivano solo
 * via Notify, frammentate, e vengono ricomposte dal protocol module.
 */
import { Capacitor } from "@capacitor/core";
import {
  buildCommand,
  bytesToHex,
  CREDENTIALS_TIMEOUT_MS,
  CrlfNotificationBuffer,
  MAC_TIMEOUT_MS,
  normalizeMac,
  parseMacMessage,
  parseProtocolMessage,
  validateWifiMessage,
  WIFI_MAX_ATTEMPTS,
  WIFI_RETRY_DELAY_MS,
  WIFI_TIMEOUT_MS,
} from "./smartDreamProtocol";

export { buildCommand, normalizeMac } from "./smartDreamProtocol";

// UUID primario confermato dal GATT DB reale su iOS. Attenzione: 0x00FF
// (`000000FF`) e 0xFF00 (`0000FF00`) sono due service completamente diversi.
export const WIFI_SERVICE_UUID = "000000FF-0000-1000-8000-00805F9B34FB";
export const WIFI_CHARACTERISTIC_UUID =
  "0000FF01-0000-1000-8000-00805F9B34FB";
export const WIFI_SERVICE_UUIDS = [
  WIFI_SERVICE_UUID,
  "0000FF00-0000-1000-8000-00805F9B34FB",
  "000000EE-0000-1000-8000-00805F9B34FB",
  "0000EE00-0000-1000-8000-00805F9B34FB",
];
export const BLE_PLUGIN_VERSION = "7.3.2";

let _bleClient = null;
let _initialized = false;
let _initPromise = null;
let _scanRunning = false;
let _scanStopResolve = null;
const _connectInFlight = new Map();
const _disconnectListeners = new Map();

function subscribeToDisconnect(deviceId, callback) {
  const listeners = _disconnectListeners.get(deviceId) || new Set();
  listeners.add(callback);
  _disconnectListeners.set(deviceId, listeners);
  return () => {
    listeners.delete(callback);
    if (listeners.size === 0) _disconnectListeners.delete(deviceId);
  };
}

function notifyDisconnected(deviceId) {
  for (const listener of _disconnectListeners.get(deviceId) || []) {
    try {
      listener(deviceId);
    } catch {
      /* diagnostics listeners must not interrupt CoreBluetooth */
    }
  }
}

function isNative() {
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android";
}

async function getClient() {
  if (_bleClient) return _bleClient;
  if (!isNative()) return null;
  const mod = await import("@capacitor-community/bluetooth-le");
  _bleClient = mod.BleClient;
  return _bleClient;
}

export async function initialize() {
  if (_initialized) return { ok: true };
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    const ble = await getClient();
    if (!ble) return { ok: false, reason: "not_native" };
    try {
      await ble.initialize({ androidNeverForLocation: true });
      _initialized = true;
      return { ok: true };
    } catch (e) {
      _initPromise = null;
      return { ok: false, reason: "init_failed", error: String(e) };
    }
  })();
  return _initPromise;
}

/**
 * Helper: avvolge `promise` con un timeout esplicito.
 * Reject con `Error("timeout_<label>_<ms>ms")` se `ms` scade prima che
 * `promise` risolva/rejetti. Usato per proteggere chiamate native BLE
 * che possono rimanere appese (es. pre-disconnect di un device mai
 * connesso, getServices su firmware lento, ecc.).
 */
function withTimeout(promise, ms, label = "op") {
  let t;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      t = setTimeout(
        () => reject(new Error(`timeout_${label}_${ms}ms`)),
        ms
      );
    }),
  ]).finally(() => {
    if (t) clearTimeout(t);
  });
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function errorText(error) {
  if (!error) return "unknown_error";
  if (typeof error === "string") return error;
  return error.message || String(error);
}

function normalizeUuid(uuid) {
  return String(uuid || "").replace(/[{}-]/g, "").toLowerCase();
}

function sameUuid(left, right) {
  return normalizeUuid(left) === normalizeUuid(right);
}

export function resolveSmartDreamGattFromServices(services) {
  const snapshot = (services || []).map((service) => ({
    uuid: service.uuid,
    characteristics: (service.characteristics || []).map((characteristic) => ({
      uuid: characteristic.uuid,
      properties: characteristic.properties || {},
    })),
  }));

  for (const service of snapshot) {
    const characteristic = service.characteristics.find((candidate) =>
      sameUuid(candidate.uuid, WIFI_CHARACTERISTIC_UUID)
    );
    if (characteristic) return { snapshot, service, characteristic };
  }
  return { snapshot, service: null, characteristic: null };
}

function emitStatus(callback, stage, details = {}) {
  if (!callback) return;
  try {
    callback({ stage, at: Date.now(), ...details });
  } catch {
    /* diagnostics must never interrupt BLE */
  }
}

export async function stopScan({ timeoutMs = 2500 } = {}) {
  const ble = await getClient();
  if (!ble) return { ok: false, reason: "not_native" };
  if (!_scanRunning) return { ok: true, alreadyStopped: true };
  try {
    await withTimeout(ble.stopLEScan(), timeoutMs, "stopScan");
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "stop_scan_failed", error: errorText(error) };
  } finally {
    _scanRunning = false;
    if (_scanStopResolve) _scanStopResolve();
    _scanStopResolve = null;
  }
}

export async function scan({ onDevice, timeoutMs = 10000 } = {}) {
  const ble = await getClient();
  if (!ble) return { ok: false, reason: "not_native", count: 0 };
  const init = await initialize();
  if (!init.ok)
    return { ok: false, reason: init.reason || "init_failed", count: 0 };

  if (_scanRunning) await stopScan();

  let count = 0;
  let resolveStopped;
  const stopped = new Promise((resolve) => {
    resolveStopped = resolve;
  });
  _scanStopResolve = resolveStopped;
  try {
    await ble.requestLEScan(
      { allowDuplicates: true, scanMode: 2 },
      (result) => {
        if (!result || !result.device) return;
        count += 1;
        try {
          onDevice &&
            onDevice({
              deviceId: result.device.deviceId,
              name:
                result.device.name ||
                result.localName ||
                result.device.deviceId ||
                "Dispositivo sconosciuto",
              rssi: typeof result.rssi === "number" ? result.rssi : null,
              advertisedServices: result.uuids || [],
              rawAdvertisement: result,
            });
        } catch {
          /* swallow */
        }
      }
    );
    _scanRunning = true;
  } catch (e) {
    _scanStopResolve = null;
    return { ok: false, reason: "scan_failed", error: String(e), count };
  }

  await Promise.race([sleep(timeoutMs), stopped]);
  if (_scanRunning) {
    await stopScan();
  }
  return { ok: true, count };
}

export async function connect(
  deviceId,
  {
    retries = 1,
    timeoutMs = 20000,
    onStatus,
    onDisconnected,
  } = {}
) {
  if (_connectInFlight.has(deviceId)) {
    emitStatus(onStatus, "connect_deduplicated", { deviceId });
    return _connectInFlight.get(deviceId);
  }

  const operation = (async () => {
  const ble = await getClient();
  if (!ble) return { ok: false, reason: "not_native" };
  const startedAt = Date.now();
  emitStatus(onStatus, "stop_scan_start", { deviceId });
  const stopped = await stopScan();
  emitStatus(onStatus, stopped.ok ? "stop_scan_done" : "stop_scan_error", {
    deviceId,
    error: stopped.error,
  });

  let lastErr = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const attemptNumber = attempt + 1;
    emitStatus(onStatus, "connection_check_start", {
      deviceId,
      attempt: attemptNumber,
    });

    // Non inviare disconnect alla cieca: su iOS una disconnect di un
    // peripheral mai connesso può restare pendente e sovrapporsi alla connect.
    // Verifichiamo prima lo stato CoreBluetooth tramite tutti i service noti.
    try {
      const connected = await withTimeout(
        ble.getConnectedDevices(WIFI_SERVICE_UUIDS),
        2500,
        "getConnectedDevices"
      );
      const stale = (connected || []).some((d) => d.deviceId === deviceId);
      emitStatus(onStatus, "connection_check_done", {
        deviceId,
        attempt: attemptNumber,
        alreadyConnected: stale,
      });
      if (stale) {
        emitStatus(onStatus, "stale_disconnect_start", {
          deviceId,
          attempt: attemptNumber,
        });
        await withTimeout(ble.disconnect(deviceId), 3000, "staleDisconnect");
        await sleep(300);
        emitStatus(onStatus, "stale_disconnect_done", {
          deviceId,
          attempt: attemptNumber,
        });
      }
    } catch (error) {
      emitStatus(onStatus, "connection_check_warning", {
        deviceId,
        attempt: attemptNumber,
        error: errorText(error),
      });
    }

    _charCache.delete(deviceId);
    _discoveryDebug.delete(deviceId);
    try {
      emitStatus(onStatus, "connect_attempt_start", {
        deviceId,
        attempt: attemptNumber,
        timeoutMs,
      });
      await withTimeout(
        ble.connect(
          deviceId,
          (disconnectedId) => {
            const resolvedId = disconnectedId || deviceId;
            emitStatus(onStatus, "native_disconnected", {
              deviceId: resolvedId,
            });
            notifyDisconnected(resolvedId);
            if (onDisconnected) onDisconnected(resolvedId);
          },
          { timeout: timeoutMs }
        ),
        timeoutMs + 3000,
        "connectWatchdog"
      );
      // CoreBluetooth può risolvere connect() prima che link ATT/MTU e cache
      // GATT siano completamente stabili. Il primo write immediato può quindi
      // fallire anche se 00FF/FF01 risultano correttamente scoperti.
      emitStatus(onStatus, "gatt_settle_start", {
        deviceId,
        attempt: attemptNumber,
        delayMs: 900,
      });
      await sleep(900);
      emitStatus(onStatus, "gatt_settle_done", {
        deviceId,
        attempt: attemptNumber,
      });
      const elapsedMs = Date.now() - startedAt;
      emitStatus(onStatus, "connect_attempt_success", {
        deviceId,
        attempt: attemptNumber,
        elapsedMs,
      });
      return { ok: true, deviceId, attempt: attemptNumber, elapsedMs };
    } catch (e) {
      lastErr = e;
      emitStatus(onStatus, "connect_attempt_error", {
        deviceId,
        attempt: attemptNumber,
        error: errorText(e),
      });
      try {
        await withTimeout(ble.disconnect(deviceId), 3000, "retryDisconnect");
      } catch {
        /* cleanup best effort */
      }
      if (attempt < retries) {
        emitStatus(onStatus, "connect_retry_wait", {
          deviceId,
          nextAttempt: attemptNumber + 1,
        });
        await sleep(800);
      }
    }
  }
  const result = {
    ok: false,
    reason: "connect_failed",
    error: errorText(lastErr),
    elapsedMs: Date.now() - startedAt,
    attempts: retries + 1,
  };
  emitStatus(onStatus, "connect_failed", { deviceId, ...result });
  return result;
  })();

  _connectInFlight.set(deviceId, operation);
  try {
    return await operation;
  } finally {
    _connectInFlight.delete(deviceId);
  }
}

/**
 * Auto-discovery: cerca FF01 nell'intero GATT DB e usa il service padre
 * realmente restituito dal dispositivo. Cache per deviceId per non ripetere.
 */
const _charCache = new Map();
// Debug della discovery per deviceId → snapshot dei service/char GATT + chosen.
const _discoveryDebug = new Map();

export function getLastDiscoveryDebug(deviceId) {
  return _discoveryDebug.get(deviceId) || null;
}

/**
 * Regola: se getServices riesce, usa esclusivamente il service enumerato che
 * contiene FF01. Il fallback 00FF/FF01 è ammesso solo se getServices fallisce,
 * mai quando il GATT DB è disponibile ma incompatibile.
 */
async function discoverSmartDreamChar(ble, deviceId) {
  if (_charCache.has(deviceId)) return _charCache.get(deviceId);

  const fallbackResult = {
    ok: true,
    serviceUuid: WIFI_SERVICE_UUID,
    characteristicUuid: WIFI_CHARACTERISTIC_UUID,
    writeWithoutResponse: false,
    canWriteWithResponse: true,
    canWriteWithoutResponse: false,
    canNotify: true,
    canIndicate: false,
    canRead: true,
    matchScore: 4,
    matchedPreferredService: true,
    fallbackServiceAsChar: false,
    fixedUuids: true,
    dynamicDiscovery: false,
    discoveryFallback: true,
  };
  let snapshot = [];
  let discoveryError = null;
  try {
    const services = await withTimeout(
      ble.getServices(deviceId),
      3000,
      "getServices"
    );
    const resolved = resolveSmartDreamGattFromServices(services);
    snapshot = resolved.snapshot;
    const selectedService = resolved.service;
    const selectedCharacteristic = resolved.characteristic;

    if (!selectedService || !selectedCharacteristic) {
      const failed = {
        ok: false,
        reason: "gatt_ff01_not_found",
        error: "FF01 non trovata nel GATT DB restituito dal dispositivo",
      };
      _discoveryDebug.set(deviceId, {
        services: snapshot,
        chosen: null,
        allCandidates: WIFI_SERVICE_UUIDS,
        error: failed.error,
      });
      return failed;
    }

    const props = selectedCharacteristic.properties || {};
    const result = {
      ok: true,
      serviceUuid: selectedService.uuid,
      characteristicUuid: selectedCharacteristic.uuid,
      writeWithoutResponse:
        !Boolean(props.write) && Boolean(props.writeWithoutResponse),
      canWriteWithResponse: Boolean(props.write),
      canWriteWithoutResponse: Boolean(props.writeWithoutResponse),
      canNotify: Boolean(props.notify),
      canIndicate: Boolean(props.indicate),
      canRead: Boolean(props.read),
      matchScore: 5,
      matchedPreferredService: WIFI_SERVICE_UUIDS.some((candidate) =>
        sameUuid(candidate, selectedService.uuid)
      ),
      fallbackServiceAsChar: false,
      fixedUuids: false,
      dynamicDiscovery: true,
      discoveryFallback: false,
    };
    console.log(
      "[BLE] getServices → FF01 risolta dinamicamente:",
      selectedService.uuid,
      "/",
      selectedCharacteristic.uuid,
      "su",
      snapshot.length,
      "service"
    );
    _charCache.set(deviceId, result);
    _discoveryDebug.set(deviceId, {
      services: snapshot,
      chosen: {
        serviceUuid: result.serviceUuid,
        characteristicUuid: result.characteristicUuid,
        matchScore: result.matchScore,
        fallbackServiceAsChar: result.fallbackServiceAsChar,
        writeWithoutResponse: result.writeWithoutResponse,
        canWriteWithResponse: result.canWriteWithResponse,
        canWriteWithoutResponse: result.canWriteWithoutResponse,
        canNotify: result.canNotify,
        canIndicate: result.canIndicate,
        canRead: result.canRead,
        fixedUuids: result.fixedUuids,
        dynamicDiscovery: result.dynamicDiscovery,
        discoveryFallback: result.discoveryFallback,
      },
      allCandidates: WIFI_SERVICE_UUIDS,
    });
    return result;
  } catch (e) {
    discoveryError = errorText(e);
    console.warn(
      "[BLE] getServices fallita; fallback al service reale 00FF/FF01:",
      e
    );
  }

  _charCache.set(deviceId, fallbackResult);
  _discoveryDebug.set(deviceId, {
    services: snapshot,
    chosen: {
      serviceUuid: fallbackResult.serviceUuid,
      characteristicUuid: fallbackResult.characteristicUuid,
      matchScore: fallbackResult.matchScore,
      fallbackServiceAsChar: fallbackResult.fallbackServiceAsChar,
      writeWithoutResponse: fallbackResult.writeWithoutResponse,
      canWriteWithResponse: fallbackResult.canWriteWithResponse,
      canWriteWithoutResponse: fallbackResult.canWriteWithoutResponse,
      canNotify: fallbackResult.canNotify,
      canIndicate: fallbackResult.canIndicate,
      canRead: fallbackResult.canRead,
      fixedUuids: true,
      dynamicDiscovery: false,
      discoveryFallback: true,
    },
    allCandidates: WIFI_SERVICE_UUIDS,
    error: discoveryError,
  });
  return fallbackResult;
}

function redactKnownSecrets(value, secrets = []) {
  let output = String(value || "");
  for (const secret of secrets.filter(Boolean)) {
    output = output.split(String(secret)).join("****");
  }
  return output.replace(/(['"]psswd['"]\s*:\s*['"])[^'"]*/gi, "$1****");
}

function containsSecretMaterial(value, secrets = []) {
  const text = String(value || "");
  if (/['"]psswd['"]\s*:/.test(text)) return true;
  return secrets.filter(Boolean).some((secretValue) => {
    const secret = String(secretValue);
    if (text.includes(secret)) return true;
    if (secret.length < 4) return false;
    for (let index = 0; index <= secret.length - 4; index += 1) {
      if (text.includes(secret.slice(index, index + 4))) return true;
    }
    return false;
  });
}

async function stopCommandNotifications(ble, deviceId, disco, phase) {
  try {
    await withTimeout(
      ble.stopNotifications(deviceId, disco.serviceUuid, disco.characteristicUuid),
      2500,
      `stopNotifications_${phase}`
    );
  } catch {
    /* cleanup best effort */
  }
}

async function executeNotificationCommand(
  ble,
  deviceId,
  disco,
  {
    phase,
    attempt,
    command,
    timeoutMs,
    subscribeDelayMs = 300,
    onStatus,
    onNotification,
    onWriteAttempt,
    onMessage,
    disconnectAfterWriteIsSuccess = false,
    diagnosticSecrets = [],
    redactNotificationChunks = false,
  }
) {
  if (!disco.canNotify) {
    return { ok: false, reason: "notify_not_supported" };
  }
  if (!disco.canWriteWithResponse) {
    return { ok: false, reason: "write_with_response_not_supported" };
  }

  const buffer = new CrlfNotificationBuffer();
  let finished = false;
  let writeStarted = false;
  let commandStarted = false;
  let timer = null;
  let resolveResult;
  const resultPromise = new Promise((resolve) => {
    resolveResult = resolve;
  });
  const finish = (result) => {
    if (finished) return;
    finished = true;
    if (timer) clearTimeout(timer);
    resolveResult(result);
  };
  const unsubscribeDisconnect = subscribeToDisconnect(deviceId, () => {
    if (writeStarted && disconnectAfterWriteIsSuccess) {
      emitStatus(onStatus, `${phase}_disconnected_after_write`, {
        deviceId,
        attempt,
      });
      finish({ ok: true, outcome: "unknown", disconnectedAfterWrite: true });
    } else {
      finish({ ok: false, reason: "disconnected_during_command" });
    }
  });

  try {
    emitStatus(onStatus, `${phase}_notify_subscribe_start`, {
      deviceId,
      attempt,
    });
    await withTimeout(
      ble.startNotifications(
        deviceId,
        disco.serviceUuid,
        disco.characteristicUuid,
        (value) => {
          if (finished) return;
          let received;
          try {
            received = buffer.push(value);
          } catch (error) {
            emitStatus(onStatus, `${phase}_buffer_error`, {
              deviceId,
              attempt,
              error: errorText(error),
            });
            finish({ ok: false, reason: "notification_buffer_error" });
            return;
          }

          const chunkText = redactNotificationChunks
            ? "****"
            : redactKnownSecrets(received.text, diagnosticSecrets);
          const chunkHex = redactNotificationChunks ? "****" : received.hex;
          const reconstructed = redactNotificationChunks
            ? "****"
            : redactKnownSecrets(received.reconstructedBuffer, diagnosticSecrets);
          const event = {
            deviceId,
            attempt,
            byteLength: received.byteLength,
            hex: chunkHex,
            text: chunkText,
            reconstructedBuffer: reconstructed,
          };
          emitStatus(onStatus, `${phase}_notify_received`, event);
          if (onNotification) onNotification({ at: Date.now(), ...event });

          if (!commandStarted) return;
          for (const message of received.messages) {
            const containsSecret = diagnosticSecrets.some(
              (secret) => secret && message.text.includes(String(secret))
            );
            const containsPasswordField = /['"]psswd['"]\s*:/.test(message.text);
            const redactMessage =
              redactNotificationChunks || containsSecret || containsPasswordField;
            emitStatus(onStatus, `${phase}_message_reassembled`, {
              deviceId,
              attempt,
              message: redactMessage
                ? "****"
                : redactKnownSecrets(message.text, diagnosticSecrets),
              messageHex: redactMessage ? "****" : message.hex,
            });
            try {
              const decision = onMessage ? onMessage(message) : null;
              if (decision?.done) finish(decision.result);
            } catch (error) {
              emitStatus(onStatus, `${phase}_message_handler_error`, {
                deviceId,
                attempt,
                error: errorText(error),
              });
            }
          }
        }
      ),
      5000,
      `startNotifications_${phase}`
    );
    emitStatus(onStatus, `${phase}_notify_subscribed`, { deviceId, attempt });
    await sleep(subscribeDelayMs);
    if (finished) return await resultPromise;
    buffer.reset();
    commandStarted = true;

    emitStatus(onStatus, `${phase}_write_start`, {
      deviceId,
      attempt,
      mode: "write",
      payload: command.redactedText,
      commandHex: phase === "credentials" ? "****" : command.hex,
    });
    try {
      writeStarted = true;
      await withTimeout(
        ble.write(
          deviceId,
          disco.serviceUuid,
          disco.characteristicUuid,
          command.view
        ),
        5000,
        `write_${phase}`
      );
      const writeInfo = { ok: true, mode: "write", attempt };
      if (onWriteAttempt) onWriteAttempt(writeInfo);
      emitStatus(onStatus, `${phase}_write_success`, {
        deviceId,
        attempt,
        mode: "write",
      });
    } catch (error) {
      const writeInfo = { ok: false, mode: "write", error: errorText(error), attempt };
      if (onWriteAttempt) onWriteAttempt(writeInfo);
      emitStatus(onStatus, `${phase}_write_error`, {
        deviceId,
        attempt,
        error: writeInfo.error,
      });
      finish({ ok: false, reason: "write_failed", error: writeInfo.error });
    }

    if (!finished) {
      timer = setTimeout(() => {
        const bufferedText = buffer.bufferText;
        const redactBuffered =
          redactNotificationChunks &&
          containsSecretMaterial(bufferedText, diagnosticSecrets);
        emitStatus(onStatus, `${phase}_response_timeout`, {
          deviceId,
          attempt,
          timeoutMs,
          hex: redactBuffered ? "****" : bytesToHex(buffer.buffer),
          reconstructedBuffer: redactBuffered
            ? "****"
            : redactKnownSecrets(bufferedText, diagnosticSecrets),
        });
        finish({ ok: false, reason: `${phase}_response_timeout` });
      }, timeoutMs);
    }

    return await resultPromise;
  } catch (error) {
    const reason = errorText(error);
    emitStatus(onStatus, `${phase}_notify_subscribe_error`, {
      deviceId,
      attempt,
      error: reason,
    });
    return { ok: false, reason: "notify_subscribe_failed", error: reason };
  } finally {
    if (timer) clearTimeout(timer);
    unsubscribeDisconnect();
    await stopCommandNotifications(ble, deviceId, disco, phase);
  }
}

export async function readMac(
  deviceId,
  { timeoutMs = MAC_TIMEOUT_MS, subscribeDelayMs = 300, onStatus } = {}
) {
  const ble = await getClient();
  if (!ble) return { ok: false, reason: "not_native" };
  emitStatus(onStatus, "gatt_discovery_start", { deviceId });
  const disco = await discoverSmartDreamChar(ble, deviceId);
  if (!disco.ok) {
    emitStatus(onStatus, "gatt_discovery_error", {
      deviceId,
      error: disco.error || disco.reason,
    });
    return disco;
  }
  emitStatus(onStatus, "gatt_discovery_done", {
    deviceId,
    serviceUuid: disco.serviceUuid,
    characteristicUuid: disco.characteristicUuid,
    canWriteWithResponse: disco.canWriteWithResponse,
    canNotify: disco.canNotify,
  });

  const command = buildCommand("mac");
  return executeNotificationCommand(ble, deviceId, disco, {
    phase: "mac",
    attempt: 1,
    command,
    timeoutMs,
    subscribeDelayMs,
    onStatus,
    onMessage: (message) => {
      const parsed = parseMacMessage(message.text);
      emitStatus(onStatus, "mac_validation_result", {
        deviceId,
        attempt: 1,
        valid: parsed.valid,
        reason: parsed.reason || "valid",
      });
      return parsed.valid
        ? { done: true, result: { ok: true, mac: parsed.mac } }
        : { done: false };
    },
  });
}

export async function scanDeviceWifiNetworks(
  deviceId,
  {
    onNetwork,
    onRawFrame,
    onWriteAttempt,
    onStatus,
    durationMs = WIFI_TIMEOUT_MS,
    subscribeDelayMs = 300,
    maxAttempts = WIFI_MAX_ATTEMPTS,
    retryDelayMs = WIFI_RETRY_DELAY_MS,
  } = {}
) {
  const ble = await getClient();
  if (!ble) return { ok: false, reason: "not_native", networks: [], rawFrames: [] };
  const disco = await discoverSmartDreamChar(ble, deviceId);
  if (!disco.ok) return { ...disco, networks: [], rawFrames: [] };

  const rawFrames = [];
  const validationFailures = [];
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    emitStatus(onStatus, "wifi_attempt_start", { deviceId, attempt, maxAttempts });
    const command = buildCommand("wifi");
    const result = await executeNotificationCommand(ble, deviceId, disco, {
      phase: "wifi",
      attempt,
      command,
      timeoutMs: durationMs,
      subscribeDelayMs,
      onStatus,
      onWriteAttempt,
      onNotification: (frame) => {
        rawFrames.push(frame);
        if (onRawFrame) onRawFrame(frame);
      },
      onMessage: (message) => {
        const validation = validateWifiMessage(message.text);
        emitStatus(onStatus, "wifi_validation_result", {
          deviceId,
          attempt,
          valid: validation.valid,
          reason: validation.reason,
          networkCount: validation.networks.length,
          reconstructedMessage: message.text,
        });
        return {
          done: true,
          result: validation.valid
            ? { ok: true, networks: validation.networks, validation }
            : {
                ok: false,
                reason: "wifi_message_invalid",
                validation,
              },
        };
      },
    });

    if (result.ok) {
      for (const network of result.networks) {
        if (onNetwork) onNetwork(network);
      }
      return { ...result, rawFrames, attempts: attempt, validationFailures };
    }

    if (!["wifi_message_invalid", "wifi_response_timeout"].includes(result.reason)) {
      return { ...result, networks: [], rawFrames, validationFailures };
    }
    validationFailures.push({
      attempt,
      reason: result.validation?.reason || result.reason,
    });
    emitStatus(onStatus, "wifi_attempt_invalid", {
      deviceId,
      attempt,
      reason: validationFailures[validationFailures.length - 1].reason,
    });
    if (attempt < maxAttempts) {
      emitStatus(onStatus, "wifi_retry_wait", {
        deviceId,
        attempt,
        delayMs: retryDelayMs,
      });
      await sleep(retryDelayMs);
    }
  }

  return {
    ok: false,
    reason: "wifi_list_incomplete",
    message: "Elenco reti non completo",
    networks: [],
    rawFrames,
    validationFailures,
    attempts: maxAttempts,
  };
}

function classifyCredentialResponse(message) {
  try {
    const parsed = parseProtocolMessage(message);
    const status = String(
      parsed?.status ?? parsed?.result ?? parsed?.wifi ?? parsed?.message ?? ""
    ).toLowerCase();
    if (/^(ok|success|connected|true)$/.test(status)) {
      return { outcome: "accepted", parsed };
    }
    if (/error|fail|rejected|invalid/.test(status)) {
      return { outcome: "rejected", parsed };
    }
    return { outcome: "unknown", parsed };
  } catch {
    return { outcome: "unknown", parsed: null };
  }
}

export async function sendWifiCredentials(
  deviceId,
  { ssid, password, onStatus, onRawFrame, timeoutMs = CREDENTIALS_TIMEOUT_MS }
) {
  const ble = await getClient();
  if (!ble) return { ok: false, reason: "not_native" };
  const disco = await discoverSmartDreamChar(ble, deviceId);
  if (!disco.ok) return disco;

  const command = buildCommand("credentials", { ssid, password });
  for (const warning of command.warnings) {
    emitStatus(onStatus, "credentials_unescaped_character_warning", {
      deviceId,
      ...warning,
    });
  }

  const result = await executeNotificationCommand(ble, deviceId, disco, {
    phase: "credentials",
    attempt: 1,
    command,
    timeoutMs,
    subscribeDelayMs: 300,
    onStatus,
    diagnosticSecrets: [password],
    redactNotificationChunks: true,
    disconnectAfterWriteIsSuccess: true,
    onNotification: (frame) => {
      if (onRawFrame) onRawFrame({ ...frame, hex: "****", text: "****" });
    },
    onMessage: (message) => {
      const safeHex = containsSecretMaterial(message.text, [password])
        ? "****"
        : message.hex;
      emitStatus(onStatus, "credentials_response_hex", {
        deviceId,
        attempt: 1,
        hex: safeHex,
      });
      const classified = classifyCredentialResponse(message.text);
      return {
        done: true,
        result:
          classified.outcome === "rejected"
            ? { ok: false, reason: "credentials_rejected", outcome: "rejected" }
            : { ok: true, outcome: classified.outcome },
      };
    },
  });

  if (result.reason === "credentials_response_timeout") {
    return { ok: true, outcome: "unknown", responseTimedOut: true };
  }
  return {
    ...result,
    serviceUuid: disco.serviceUuid,
    characteristicUuid: disco.characteristicUuid,
  };
}

export async function disconnect(deviceId) {
  const ble = await getClient();
  if (!ble || !deviceId) return;
  try {
    await withTimeout(ble.disconnect(deviceId), 4000, "disconnect");
  } catch {
    /* ignore */
  }
  _charCache.delete(deviceId);
  _discoveryDebug.delete(deviceId);
}

export const _config = {
  isNative,
  WIFI_SERVICE_UUID,
  WIFI_CHARACTERISTIC_UUID,
  WIFI_SERVICE_UUIDS,
  BLE_PLUGIN_VERSION,
};
